import styled from "@emotion/styled";
import { 
    Box, 
    Typography, 
    Stack, 
    Button, 
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogActions, 
    TextField,
    MenuItem,
    Chip,
    CircularProgress,
    Alert,
    Skeleton,
    Switch,
    Autocomplete,
    Checkbox,
    FormControlLabel
} from "@mui/material";
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { useState, useEffect, useMemo, useRef } from "react";
import { 
    DataGridPremium, 
    GridToolbarContainer,
    useGridApiRef,
    gridClasses 
} from "@mui/x-data-grid-premium";
import { alpha, lighten, darken } from "@mui/material/styles";
import { LicenseInfo } from "@mui/x-license";
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import LockIcon from '@mui/icons-material/Lock';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import Select from '@mui/material/Select';
import { 
    useGetXranHeadersQuery, 
    useGetXranSystemsQuery,
    useUpdateXranFieldMutation,
    useDeleteXranSystemMutation,
    useCreateXranSystemMutation,
    useGetXranColumnPreferencesQuery,
    useUpsertXranColumnPreferencesMutation
} from "api/homeApi";

// License for DataGridPremium
LicenseInfo.setLicenseKey(
    "7b2a7be60c5eff8e15f1f2ae43e1260dTz0xMjMyNTksRT0xNzk3NDY1NTk5MDAwLFM9cHJlbWl1bSxMTT1zdWJzY3JpcHRpb24sUFY9aW5pdGlhbCxLVj0y"
);

// Styled Components
const PageContainer = styled(Box)(({ theme }) => ({
    padding: "0.75rem",
    backgroundColor: "#f8f9fa",
    minHeight: "100vh",
    overflow: "hidden",
    boxSizing: "border-box",
    maxWidth: "calc(100vw - 85px)",
}));

const HeaderSection = styled(Box)(({ theme }) => ({
    marginBottom: "1rem",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "1rem",
}));

const PageTitle = styled(Typography)(({ theme }) => ({
    fontFamily: "Montserrat",
    fontWeight: 700,
    color: "#0a2240",
    fontSize: "24px",
}));

const StyledDataGridPremium = styled(DataGridPremium)(({ theme }) => ({
    [`& .${gridClasses.row}.even`]: {
        backgroundColor: lighten("#0a2240", 0.95),
        '&:hover': {
            backgroundColor: lighten("#0a2240", 0.9),
        },
    },
    [`& .${gridClasses.row}.odd`]: {
        backgroundColor: "white",
        '&:hover': {
            backgroundColor: lighten("#0a2240", 0.95),
        },
    },
    "& .MuiDataGrid-cell": {
        padding: "4px 8px !important",
    },
    // Column header styling
    "& .MuiDataGrid-columnHeader": {
        backgroundColor: "#0a2240",
        color: "#fff !important",
        fontWeight: 600,
    },
    // All icons inside column headers must be white
    "& .MuiDataGrid-columnHeader .MuiSvgIcon-root": {
        color: "#fff !important",
    },
    "& .MuiDataGrid-columnHeader .MuiIconButton-root": {
        color: "#fff !important",
    },
    // Sort icon — navy so it's visible on the white circle bg
    "& .MuiDataGrid-sortIcon": {
        color: "#0a2240 !important",
        opacity: "1 !important",
    },
    "& .MuiDataGrid-iconButtonContainer": {
        visibility: "visible !important",
        width: "auto !important",
        "& .MuiSvgIcon-root": {
            color: "#0a2240 !important",
        },
    },
    "& .MuiDataGrid-filterIcon": {
        color: "#fff !important",
    },
    "& .MuiDataGrid-columnHeader .MuiDataGrid-columnSeparator": {
        color: "rgba(255,255,255,0.3)",
    },
    // Header filter row (below column headers) => white bg
    "& .MuiDataGrid-headerFilterRow .MuiDataGrid-columnHeader": {
        backgroundColor: "#fff !important",
    },
    "& .MuiDataGrid-headerFilterRow .MuiSvgIcon-root": {
        color: "#0a2240 !important",
    },
    "& .MuiDataGrid-headerFilterRow .MuiInputBase-root": {
        backgroundColor: "#fff !important",
    },
    // Toolbar
    "& .MuiDataGrid-toolbarContainer": {
        padding: "1rem",
        backgroundColor: "#f8f9fa",
        borderBottom: "1px solid #e0e0e0",
    },
    "& .MuiDataGrid-toolbarContainer .MuiButtonBase-root": {
        color: "#005AA6",
    },
    // Dimmed columns (not highlighted)
    "& .dimmed-cell": {
        backgroundColor: "rgba(0, 0, 0, 0.04) !important",
        opacity: 0.55,
    },
    "& .dimmed-header": {
        backgroundColor: alpha("#0a2240", 0.55) + " !important",
    },
}));

const CreateSystemButton = styled(Button)(({ theme }) => ({
    backgroundColor: "#0a2240",
    color: "white",
    fontWeight: 600,
    textTransform: "none",
    padding: "10px 20px",
    borderRadius: "4px",
    "&:hover": {
        backgroundColor: darken("#0a2240", 0.1),
    },
}));

const ActionButton = styled(Button)(({ theme }) => ({
    textTransform: "none",
    borderRadius: "4px",
    fontWeight: 500,
    "&:hover": {
        opacity: 0.8,
    },
}));

const FormField = styled(TextField)(({ theme }) => ({
    marginBottom: "1rem",
    width: "100%",
    "& .MuiOutlinedInput-root": {
        borderRadius: "4px",
    },
}));

const FormGrid = styled(Box)(({ theme }) => ({
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "1rem",
    "@media (max-width: 600px)": {
        gridTemplateColumns: "1fr",
    },
}));

// Helper: Parse choices string to array
const parseChoices = (choicesString) => {
    if (!choicesString) return null;
    return choicesString.split(',').map(c => c.trim());
};

// Helper: Convert value to array if comma-separated
// eslint-disable-next-line no-unused-vars
const parseValue = (value) => {
    if (!value) return null;
    if (Array.isArray(value)) return value;
    if (typeof value === 'string' && value.includes(',')) {
        return value.split(',').map(v => v.trim());
    }
    return value;
};

const XRAN_PAGE_KEY = 'xran-tracking';

const arraysEqual = (a, b) => {
    if (!Array.isArray(a) || !Array.isArray(b)) return false;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i += 1) {
        if (a[i] !== b[i]) return false;
    }
    return true;
};

const MAX_VISIBLE_COLUMN_TAGS = 3;

// Component
export const XranTracking = ({ user }) => {
    const apiRef = useGridApiRef();

    // Check if user can edit (NOC, admin, or special users)
    const userRoles = user?.role || [];
    const username = user?.username || '';
    const userIdentifier = user?.sub || '';
    const EDIT_USERS = ['cthorpe'];
    const canEdit = userRoles.includes('admin') || userRoles.includes('NOC') || EDIT_USERS.includes(username);

    // Queries
    const { data: headersResponse, isLoading: headersLoading } = useGetXranHeadersQuery();
    const { data: systemsResponse, isLoading: systemsLoading, refetch: refetchSystems } = useGetXranSystemsQuery();
    const {
        data: preferencesResponse,
        isLoading: isPreferencesLoading,
        isFetching: isPreferencesFetching,
        isUninitialized: isPreferencesUninitialized,
        refetch: refetchPreferences,
    } = useGetXranColumnPreferencesQuery(
        { userIdentifier, pageKey: XRAN_PAGE_KEY },
        { skip: !userIdentifier }
    );
    
    // Mutations
    const [updateField, { isLoading: updatingField }] = useUpdateXranFieldMutation();
    const [deleteSystem, { isLoading: deletingSystem }] = useDeleteXranSystemMutation();
    const [createSystem, { isLoading: creatingSystem }] = useCreateXranSystemMutation();
    const [upsertXranColumnPreferences] = useUpsertXranColumnPreferencesMutation();

    // Local States
    const [rows, setRows] = useState([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [newSystemForm, setNewSystemForm] = useState({});
    const [snackbar, setSnackbar] = useState({ open: false, message: "", type: "success" });
    const [visibleColumnFields, setVisibleColumnFields] = useState([]);
    // eslint-disable-next-line no-unused-vars
    const [isPreferencesInitialized, setIsPreferencesInitialized] = useState(false);
    const [isSavingPreferences, setIsSavingPreferences] = useState(false);
    const lastSavedVisibleColumnsRef = useRef([]);

    // Extract headers array from response
    const headersData = useMemo(() => {
        const headers = headersResponse?.headers || [];
        // Map header labels to desired display names
        const labelMap = {
            'Number of Radios': 'Number of CBSDs'
        };
        // Map field names to additional options to append
        const additionalOptions = {
            'current_software_release': ['SR02.10.2'],
            'target_software_release': ['SR02.10.2'],
            'current_druid_mocn': ['5.2.12.4 p1']
        };
        return headers.map(header => {
            let choices = header.choices;
            // Append additional options if defined for this field
            if (additionalOptions[header.name]) {
                const existingChoices = parseChoices(choices) || [];
                const additionalChoices = additionalOptions[header.name];
                const combined = [...new Set([...existingChoices, ...additionalChoices])];
                choices = combined.join(',');
            }
            return {
                ...header,
                label: labelMap[header.label] || header.label,
                choices
            };
        });
    }, [headersResponse]);

    // Extract systems array from response
    const systemsData = useMemo(() => {
        return systemsResponse?.rows || [];
    }, [systemsResponse]);

    const availableColumnFields = useMemo(() => {
        return headersData
            .filter((h) => h.name !== 'id')
            .map((h) => h.name);
    }, [headersData]);

    // Initialize rows from systemsData
    useEffect(() => {
        if (systemsData && Array.isArray(systemsData)) {
            const formattedRows = systemsData.map((system) => ({
                ...system,
            }));
            setRows(formattedRows);
        }
    }, [systemsData]);

    useEffect(() => {
        if (!availableColumnFields.length) return;

        // Evita di aggiornare mentre la query è in corso o durante il salvataggio ottimistico
        if (userIdentifier && (isPreferencesUninitialized || isPreferencesLoading || isPreferencesFetching || isSavingPreferences)) {
            return;
        }

        const visibleColumnsPayload = Array.isArray(preferencesResponse?.visible_columns)
            ? preferencesResponse.visible_columns
            : Array.isArray(preferencesResponse?.data?.visible_columns)
                ? preferencesResponse.data.visible_columns
                : null;

        // Se il backend ha risposto con una lista (anche vuota), usa quella
        // Altrimenti (primo caricamento), mostra tutte le colonne di default
        let nextVisibleColumns;
        if (visibleColumnsPayload !== null) {
            // Backend ha fornito una lista (possibilmente vuota)
            nextVisibleColumns = visibleColumnsPayload.filter((field) => availableColumnFields.includes(field));
        } else {
            // Nessuna preferenza dal backend, mostra tutte di default
            nextVisibleColumns = availableColumnFields;
        }

        // Aggiorna solo se la risposta backend è diversa sia dalla selezione locale che dall'ultima inviata
        if (
            !arraysEqual(nextVisibleColumns, visibleColumnFields) &&
            !arraysEqual(nextVisibleColumns, lastSavedVisibleColumnsRef.current)
        ) {
            setVisibleColumnFields(nextVisibleColumns);
            lastSavedVisibleColumnsRef.current = nextVisibleColumns;
        }
        setIsPreferencesInitialized(true);
    }, [
        availableColumnFields,
        preferencesResponse,
        userIdentifier,
        isPreferencesUninitialized,
        isPreferencesLoading,
        isPreferencesFetching,
        visibleColumnFields,
        isSavingPreferences
    ]);



    // Initialize form with empty values based on headers
    useEffect(() => {
        if (headersData && Array.isArray(headersData)) {
            const initialForm = {};
            headersData.forEach((header) => {
                if (header.type === "boolean") {
                    initialForm[header.name] = false;
                } else if (header.multiple_values) {
                    initialForm[header.name] = [];
                } else {
                    initialForm[header.name] = "";
                }
            });
            setNewSystemForm(initialForm);
        }
    }, [headersData]);

    // Build columns dynamically from headers
    const columns = useMemo(() => {
        if (!headersData || headersData.length === 0) return [];

        return headersData
            .filter(header => header.name !== 'id') // Skip ID column
            .map((header) => {
                const columnConfig = {
                    field: header.name,
                    headerName: header.label || header.name,
                    minWidth: Math.max(140, (header.label || header.name).length * 10 + 50),
                    editable: canEdit,
                    sortable: true,
                    filterable: true,
                };

                // Parse choices from string to array
                const options = parseChoices(header.choices);

                // Handle different field types
                if (header.type === "boolean") {
                    columnConfig.editable = false; // Toggle handles it
                    columnConfig.renderCell = (params) => (
                        <BooleanToggleCell
                            value={params.value}
                            disabled={!canEdit}
                            onChange={(newVal) => handleCellUpdate(params.row.id, header.name, newVal)}
                        />
                    );
                } else if (header.type === "integer") {
                    columnConfig.editable = false;
                    columnConfig.renderCell = (params) => (
                        <InlineTextCell
                            value={params.value}
                            editable={canEdit}
                            type="number"
                            onSave={(val) => handleCellUpdate(params.row.id, header.name, val)}
                        />
                    );
                } else if (header.type === "date") {
                    columnConfig.editable = false;
                    columnConfig.renderCell = (params) => (
                        <InlineDateCell
                            value={params.value}
                            editable={canEdit}
                            onSave={(val) => handleCellUpdate(params.row.id, header.name, val)}
                        />
                    );
                } else if (options) {
                    // Has choices
                    if (header.multiple_values) {
                        columnConfig.editable = false;
                        columnConfig.renderCell = (params) => (
                            <MultiSelectCell
                                value={params.value}
                                options={options}
                                editable={canEdit}
                                onSave={(newVal) => handleCellUpdate(params.row.id, header.name, newVal)}
                            />
                        );
                    } else {
                        columnConfig.editable = false;
                        columnConfig.renderCell = (params) => (
                            <InlineSelectCell
                                value={params.value}
                                options={options}
                                editable={canEdit}
                                onSave={(val) => handleCellUpdate(params.row.id, header.name, val)}
                            />
                        );
                    }
                } else {
                    // Plain text
                    columnConfig.editable = false;
                    columnConfig.renderCell = (params) => (
                        <InlineTextCell
                            value={params.value}
                            editable={canEdit}
                            onSave={(val) => handleCellUpdate(params.row.id, header.name, val)}
                        />
                    );
                }

                return columnConfig;
            });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handleCellUpdate is declared after this memo; adding it would throw a TDZ error
    }, [headersData, canEdit]);

    // Show only the selected visible columns
    const columnsWithActions = useMemo(() => {
        const visibleSet = new Set(visibleColumnFields);
        const filteredColumns = columns.filter((col) => visibleSet.has(col.field));

        if (!canEdit) return filteredColumns;
        const actionsCol = {
            field: "actions",
            headerName: "Actions",
            width: 100,
            sortable: false,
            filterable: false,
            editable: false,
            pinned: 'left',
            renderCell: (params) => (
                <Stack direction="row" spacing={0.5}>
                    <ActionButton
                        size="small"
                        variant="contained"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={() => handleDeleteSystem(params.row.id)}
                        disabled={deletingSystem}
                    >
                        Delete
                    </ActionButton>
                </Stack>
            ),
        };
        return [actionsCol, ...filteredColumns];
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handleDeleteSystem is declared after this memo; adding it would throw a TDZ error
    }, [columns, deletingSystem, canEdit, visibleColumnFields]);

    const handleCellUpdate = async (systemId, fieldName, newValue) => {
        try {
            // Convert array to comma-separated string if needed
            let valueToSend = newValue;
            if (Array.isArray(newValue)) {
                valueToSend = newValue.join(",");
            } else if (typeof newValue === 'boolean') {
                valueToSend = newValue;
            } else if (newValue instanceof Date) {
                const yyyy = newValue.getFullYear();
                const mm = String(newValue.getMonth() + 1).padStart(2, '0');
                const dd = String(newValue.getDate()).padStart(2, '0');
                valueToSend = `${yyyy}-${mm}-${dd}`;
            } else if (newValue === null || newValue === undefined) {
                valueToSend = "";
            }

            // Optimistically update local rows so columns don't shift
            setRows(prev => prev.map(r =>
                r.id === systemId ? { ...r, [fieldName]: Array.isArray(newValue) ? newValue.join(',') : newValue } : r
            ));

            await updateField({
                id: systemId,
                field: fieldName,
                newvalue: valueToSend,
            }).unwrap();

            showSnackbar("Field updated successfully", "success");
        } catch (error) {
            console.error("Error updating field:", error);
            showSnackbar("Error updating field", "error");
            // Revert on error
            refetchSystems();
        }
    };

    const handleDeleteSystem = async (systemId) => {
        if (window.confirm("Are you sure you want to delete this system?")) {
            try {
                await deleteSystem({ id: systemId }).unwrap();
                showSnackbar("System deleted successfully", "success");
                refetchSystems();
            } catch (error) {
                console.error("Error deleting system:", error);
                showSnackbar("Error deleting system", "error");
            }
        }
    };

    const handleOpenDialog = () => {
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        // Reset form
        if (headersData && Array.isArray(headersData)) {
            const initialForm = {};
            headersData.forEach((header) => {
                if (header.type === "boolean") {
                    initialForm[header.name] = false;
                } else if (header.multiple_values) {
                    initialForm[header.name] = [];
                } else {
                    initialForm[header.name] = "";
                }
            });
            setNewSystemForm(initialForm);
        }
    };

    const handleFormChange = (fieldName, value) => {
        setNewSystemForm((prev) => ({
            ...prev,
            [fieldName]: value,
        }));
    };

    const handleCreateSystem = async () => {
        try {
            // Convert arrays to comma-separated strings
            const formDataToSend = {};
            Object.keys(newSystemForm).forEach((key) => {
                const value = newSystemForm[key];
                if (Array.isArray(value)) {
                    formDataToSend[key] = value.join(",");
                } else if (typeof value === 'boolean') {
                    formDataToSend[key] = value;
                } else {
                    formDataToSend[key] = value === "" ? null : value;
                }
            });

            await createSystem(formDataToSend).unwrap();
            showSnackbar("System created successfully", "success");
            handleCloseDialog();
            refetchSystems();
        } catch (error) {
            console.error("Error creating system:", error);
            showSnackbar("Error creating system", "error");
        }
    };

    const showSnackbar = (message, type) => {
        setSnackbar({ open: true, message, type });
        setTimeout(() => setSnackbar({ open: false, message: "", type: "success" }), 3000);
    };

    const handleExportCSV = () => {
        // Use the visible (filtered) row IDs from the grid
        const visibleRowIds = apiRef.current.getSortedRowIds?.() ?? rows.map(r => r.id);
        const visibleRows = visibleRowIds
            .map(id => rows.find(r => r.id === id))
            .filter(Boolean);

        // Build column list (skip the actions column)
        const exportColumns = columnsWithActions.filter(c => c.field !== 'actions');
        const headers = exportColumns.map(c => c.headerName || c.field);
        const fields = exportColumns.map(c => c.field);

        const escape = (val) => {
            if (val === null || val === undefined) return '';
            const str = String(val);
            return str.includes(',') || str.includes('"') || str.includes('\n')
                ? `"${str.replace(/"/g, '""')}"`
                : str;
        };

        const csvRows = [
            headers.map(escape).join(','),
            ...visibleRows.map(row => fields.map(f => escape(row[f])).join(',')),
        ];

        const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `xran_systems_${new Date().toISOString().slice(0, 10)}.csv`;
        link.click();
        URL.revokeObjectURL(url);
    };

    const getRowClassName = (params) => {
        return params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd";
    };

    if (headersLoading) {
        return (
            <PageContainer>
                <HeaderSection>
                    <Skeleton variant="text" width={300} height={40} />
                    <Skeleton variant="rectangular" width={150} height={40} />
                </HeaderSection>
                <Skeleton variant="rectangular" height={500} />
            </PageContainer>
        );
    }

    return (
        <PageContainer>
            {snackbar.open && (
                <Alert
                    severity={snackbar.type}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    sx={{ marginBottom: "1rem" }}
                >
                    {snackbar.message}
                </Alert>
            )}

            <HeaderSection>
                <Stack direction="row" alignItems="center" spacing={2}>
                    <PageTitle>XRAN Global Systems Tracking</PageTitle>
                    <Chip
                        icon={canEdit ? <EditIcon sx={{ fontSize: '14px !important' }} /> : <LockIcon sx={{ fontSize: '14px !important' }} />}
                        label={canEdit ? 'Edit' : 'Read-only'}
                        size="small"
                        sx={{
                            backgroundColor: canEdit ? lighten('#005AA6', 0.85) : '#e0e0e0',
                            color: canEdit ? '#005AA6' : '#666',
                            fontWeight: 600,
                            fontSize: '11px',
                            height: '24px',
                        }}
                    />
                </Stack>
                <Stack direction="row" spacing={1}>
                    <Button
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={handleExportCSV}
                        sx={{
                            color: '#0a2240',
                            borderColor: '#0a2240',
                            fontWeight: 600,
                            textTransform: 'none',
                            padding: '9px 18px',
                            borderRadius: '4px',
                            '&:hover': { borderColor: '#005AA6', color: '#005AA6', backgroundColor: 'rgba(0,90,166,0.05)' },
                        }}
                    >
                        Export CSV
                    </Button>
                    {canEdit && (
                        <CreateSystemButton
                            startIcon={<AddIcon />}
                            onClick={handleOpenDialog}
                        >
                            New System
                        </CreateSystemButton>
                    )}
                </Stack>
            </HeaderSection>

            {/* Column Visibility Filter */}
            <Box sx={{
                mb: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                gap: 0.5,
            }}>
                <Typography
                    variant="body2"
                    sx={{
                        color: '#0a2240',
                        fontSize: '13px',
                        fontWeight: 600,
                        ml: 0.25,
                    }}
                >
                    Select the columns you want to see. Your choice will be saved.
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <VisibilityIcon sx={{ color: '#0a2240', fontSize: 20 }} />
                    <Autocomplete
                        multiple
                        size="small"
                        options={[{ name: '__show_all__', label: 'Select all' }, ...headersData.filter(h => h.name !== 'id')]}
                        getOptionLabel={(option) => option.label || option.name}
                        value={headersData.filter(
                            (h) => h.name !== 'id' && visibleColumnFields.includes(h.name)
                        )}
                        onChange={async (e, newVal) => {
                            // Se l'utente seleziona "Show all", mostra tutte le colonne
                            if (newVal.some(v => v.name === '__show_all__')) {
                                const allColumns = headersData.filter(h => h.name !== 'id').map(h => h.name);
                                setVisibleColumnFields(allColumns);
                                setIsSavingPreferences(true);
                                try {
                                    await upsertXranColumnPreferences({
                                        userIdentifier,
                                        pageKey: XRAN_PAGE_KEY,
                                        visibleColumns: allColumns,
                                    });
                                    lastSavedVisibleColumnsRef.current = allColumns;
                                    setTimeout(() => refetchPreferences(), 100);
                                } catch (error) {
                                    console.error('Error saving XRAN column preferences:', error);
                                } finally {
                                    setTimeout(() => setIsSavingPreferences(false), 1500);
                                }
                            } else {
                                const nextVisible = newVal.map((col) => col.name).filter(Boolean);
                                setVisibleColumnFields(nextVisible);
                                setIsSavingPreferences(true);
                                try {
                                    await upsertXranColumnPreferences({
                                        userIdentifier,
                                        pageKey: XRAN_PAGE_KEY,
                                        visibleColumns: nextVisible,
                                    });
                                    lastSavedVisibleColumnsRef.current = nextVisible;
                                    // Refetch per sincronizzare la cache e evitare che le vecchie preferenze sovrascrivano l'UI
                                    setTimeout(() => refetchPreferences(), 100);
                                } catch (error) {
                                    console.error('Error saving XRAN column preferences:', error);
                                } finally {
                                    setTimeout(() => setIsSavingPreferences(false), 1500);
                                }
                            }
                        }}
                        isOptionEqualToValue={(option, value) => option.name === value.name}
                        disableCloseOnSelect
                        limitTags={MAX_VISIBLE_COLUMN_TAGS}
                        slotProps={{
                            clearIndicator: {
                                onClick: async () => {
                                    setVisibleColumnFields([]);
                                    setIsSavingPreferences(true);
                                    try {
                                        await upsertXranColumnPreferences({
                                            userIdentifier,
                                            pageKey: XRAN_PAGE_KEY,
                                            visibleColumns: [],
                                        });
                                        lastSavedVisibleColumnsRef.current = [];
                                        setTimeout(() => refetchPreferences(), 100);
                                    } catch (error) {
                                        console.error('Error saving XRAN column preferences:', error);
                                    } finally {
                                        setTimeout(() => setIsSavingPreferences(false), 1500);
                                    }
                                },
                            },
                        }}
                        renderOption={(props, option, { selected }) => {
                            // Renderizza diversamente l'opzione "Show all"
                            if (option.name === '__show_all__') {
                                return (
                                    <li {...props} key="show_all" style={{ 
                                        borderBottom: '1px solid #e0e0e0', 
                                        marginBottom: '4px', 
                                        paddingBottom: '4px',
                                        cursor: 'pointer'
                                    }}>
                                        <Box sx={{
                                            padding: '8px 12px',
                                            backgroundColor: 'rgba(0, 90, 166, 0.08)',
                                            borderRadius: '4px',
                                            textAlign: 'center',
                                            fontWeight: 600,
                                            color: '#005AA6',
                                            fontSize: '13px',
                                            transition: 'background-color 0.2s',
                                            '&:hover': {
                                                backgroundColor: 'rgba(0, 90, 166, 0.15)',
                                            }
                                        }}>
                                            {option.label}
                                        </Box>
                                    </li>
                                );
                            }
                            return (
                                <li {...props} key={option.name}>
                                    <Checkbox
                                        icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                                        checkedIcon={<CheckBoxIcon fontSize="small" />}
                                        checked={selected}
                                        sx={{ mr: 1, '&.Mui-checked': { color: '#005AA6' } }}
                                    />
                                    {option.label || option.name}
                                </li>
                            );
                        }}
                        renderTags={(selected, getTagProps) => {
                            const visibleTags = selected.slice(0, MAX_VISIBLE_COLUMN_TAGS);
                            const hiddenCount = selected.length - visibleTags.length;

                            return [
                                ...visibleTags.map((opt, index) => (
                                    <Chip
                                        {...getTagProps({ index })}
                                        key={opt.name}
                                        label={opt.label || opt.name}
                                        size="small"
                                        sx={{
                                            backgroundColor: lighten('#005AA6', 0.7),
                                            color: '#0a2240',
                                            fontWeight: 600,
                                            height: '22px',
                                            maxWidth: 170,
                                            '& .MuiChip-label': {
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            },
                                        }}
                                    />
                                )),
                                hiddenCount > 0 ? (
                                    <Chip
                                        key="more-selected-columns"
                                        label={`+${hiddenCount} more`}
                                        size="small"
                                        sx={{
                                            backgroundColor: '#e8eef6',
                                            color: '#0a2240',
                                            fontWeight: 700,
                                            height: '22px',
                                        }}
                                    />
                                ) : null,
                            ];
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                variant="outlined"
                                placeholder={visibleColumnFields.length === 0 ? "Select columns to show..." : ""}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: 'white',
                                        borderRadius: '6px',
                                        fontSize: '13px',
                                        flexWrap: 'nowrap',
                                        overflow: 'hidden',
                                        '& fieldset': { borderColor: '#d0d5dd' },
                                        '&:hover fieldset': { borderColor: '#005AA6' },
                                        '&.Mui-focused fieldset': { borderColor: '#005AA6' },
                                    },
                                    '& .MuiAutocomplete-tag': {
                                        my: 0,
                                    },
                                    '& .MuiAutocomplete-inputRoot': {
                                        flexWrap: 'nowrap !important',
                                    },
                                }}
                            />
                        )}
                        sx={{ flex: 1, maxWidth: 900 }}
                    />
                </Box>
            </Box>

            {systemsLoading ? (
                <Skeleton variant="rectangular" height={500} />
            ) : (
                <Box sx={{ height: "calc(100vh - 190px)", width: "100%", backgroundColor: "white", borderRadius: "4px", overflow: "auto", maxWidth: "100%" }}>
                    <StyledDataGridPremium
                        apiRef={apiRef}
                        rows={rows}
                        columns={columnsWithActions}
                        getRowClassName={getRowClassName}
                        slots={{
                            toolbar: () => (
                                <GridToolbarContainer sx={{ padding: "0.8rem" }}>
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            fontWeight: 700,
                                            fontSize: "16px",
                                            color: "#0a2240",
                                        }}
                                    >
                                        Total Systems: {rows.length}
                                    </Typography>
                                    <Box sx={{ flexGrow: 1 }} />
                                    {updatingField || deletingSystem ? (
                                        <CircularProgress size={20} />
                                    ) : null}
                                </GridToolbarContainer>
                            ),
                        }}
                        slotProps={{
                            headerFilterCell: {
                                sx: {
                                    backgroundColor: "#f8f9fa",
                                },
                            },
                        }}
                        sx={{
                            height: '100%',
                        }}
                        headerFilters={true}
                        filterMode="client"
                        pagination={false}
                        disableColumnReorder
                    />
                </Box>
            )}

            {/* Create System Dialog */}
            <Dialog
                open={openDialog}
                onClose={handleCloseDialog}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle sx={{ fontWeight: 700, color: "#0a2240" }}>
                    Create New System
                </DialogTitle>
                <DialogContent sx={{ paddingTop: "2rem" }}>
                    <FormGrid>
                        {headersData &&
                            Array.isArray(headersData) &&
                            headersData
                                .filter(h => h.name !== 'id') // Skip ID in form
                                .map((header) => {
                                    const fieldValue = newSystemForm[header.name] ?? "";
                                    const options = parseChoices(header.choices);

                                    if (header.type === "boolean") {
                                        return (
                                            <FormControlLabel
                                                key={header.name}
                                                label={header.label || header.name}
                                                control={
                                                    <Switch
                                                        checked={!!fieldValue}
                                                        onChange={(e) => handleFormChange(header.name, e.target.checked)}
                                                        sx={{
                                                            '& .MuiSwitch-switchBase.Mui-checked': { color: '#005AA6' },
                                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#005AA6' },
                                                        }}
                                                    />
                                                }
                                                sx={{ width: '100%', ml: 0 }}
                                            />
                                        );
                                    } else if (header.type === "date") {
                                        return (
                                            <FormField
                                                key={header.name}
                                                type="date"
                                                label={header.label || header.name}
                                                value={fieldValue}
                                                onChange={(e) => handleFormChange(header.name, e.target.value)}
                                                InputLabelProps={{
                                                    shrink: true,
                                                }}
                                            />
                                        );
                                    } else if (header.type === "integer") {
                                        return (
                                            <FormField
                                                key={header.name}
                                                type="number"
                                                label={header.label || header.name}
                                                value={fieldValue}
                                                onChange={(e) => handleFormChange(header.name, e.target.value)}
                                            />
                                        );
                                    } else if (options) {
                                        // Has choices
                                        if (header.multiple_values) {
                                            return (
                                                <Autocomplete
                                                    key={header.name}
                                                    multiple
                                                    size="small"
                                                    options={options}
                                                    value={Array.isArray(fieldValue) ? fieldValue : []}
                                                    onChange={(e, newVal) => handleFormChange(header.name, newVal)}
                                                    disableCloseOnSelect
                                                    renderOption={(props, option, { selected }) => (
                                                        <li {...props} key={option}>
                                                            <Checkbox
                                                                icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                                                                checkedIcon={<CheckBoxIcon fontSize="small" />}
                                                                checked={selected}
                                                                sx={{ mr: 1, '&.Mui-checked': { color: '#005AA6' } }}
                                                            />
                                                            {option}
                                                        </li>
                                                    )}
                                                    renderTags={(selected, getTagProps) =>
                                                        selected.map((opt, index) => (
                                                            <Chip
                                                                {...getTagProps({ index })}
                                                                key={opt}
                                                                label={opt}
                                                                size="small"
                                                                sx={{
                                                                    backgroundColor: lighten('#005AA6', 0.7),
                                                                    color: '#0a2240',
                                                                    fontWeight: 500,
                                                                }}
                                                            />
                                                        ))
                                                    }
                                                    renderInput={(params) => (
                                                        <FormField
                                                            {...params}
                                                            label={header.label || header.name}
                                                        />
                                                    )}
                                                />
                                            );
                                        } else {
                                            return (
                                                <FormField
                                                    key={header.name}
                                                    select
                                                    label={header.label || header.name}
                                                    value={fieldValue}
                                                    onChange={(e) => handleFormChange(header.name, e.target.value)}
                                                >
                                                    <MenuItem value="">--</MenuItem>
                                                    {options.map((option) => (
                                                        <MenuItem key={option} value={option}>
                                                            {option}
                                                        </MenuItem>
                                                    ))}
                                                </FormField>
                                            );
                                        }
                                    } else {
                                        // Text field
                                        return (
                                            <FormField
                                                key={header.name}
                                                label={header.label || header.name}
                                                value={fieldValue}
                                                onChange={(e) => handleFormChange(header.name, e.target.value)}
                                                multiline={false}
                                                rows={1}
                                            />
                                        );
                                    }
                                })}
                    </FormGrid>
                </DialogContent>
                <DialogActions sx={{ padding: "1rem" }}>
                    <Button
                        onClick={handleCloseDialog}
                        variant="outlined"
                        sx={{ textTransform: "none" }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleCreateSystem}
                        variant="contained"
                        sx={{
                            backgroundColor: "#0a2240",
                            color: "white",
                            textTransform: "none",
                            "&:hover": {
                                backgroundColor: darken("#0a2240", 0.1),
                            },
                        }}
                        disabled={creatingSystem}
                    >
                        {creatingSystem ? <CircularProgress size={20} /> : "Create System"}
                    </Button>
                </DialogActions>
            </Dialog>
        </PageContainer>
    );
};

// Boolean Toggle Cell
const BooleanToggleCell = ({ value, disabled, onChange }) => {
    return (
        <Switch
            checked={!!value}
            disabled={disabled}
            onChange={(e) => onChange(e.target.checked)}
            size="small"
            sx={{
                '& .MuiSwitch-switchBase.Mui-checked': { color: '#005AA6' },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#005AA6' },
            }}
        />
    );
};

// Inline Text Cell — always shows an input when editable
const InlineTextCell = ({ value, editable, type, onSave }) => {
    const [localVal, setLocalVal] = useState(value ?? '');
    const committed = useRef(value ?? '');

    useEffect(() => {
        setLocalVal(value ?? '');
        committed.current = value ?? '';
    }, [value]);

    const commit = () => {
        const v = type === 'number' ? (localVal === '' ? null : Number(localVal)) : localVal;
        if (String(v ?? '') !== String(committed.current ?? '')) {
            committed.current = v;
            onSave(v);
        }
    };

    if (!editable) {
        return <Typography variant="body2" sx={{ fontSize: '13px' }}>{value ?? '--'}</Typography>;
    }

    return (
        <TextField
            variant="outlined"
            size="small"
            type={type || 'text'}
            value={localVal}
            onChange={(e) => setLocalVal(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === 'Enter') {
                    e.target.blur();
                }
            }}
            fullWidth
            sx={{
                '& .MuiOutlinedInput-root': {
                    fontSize: '13px',
                    padding: 0,
                    backgroundColor: '#f8fafc',
                    borderRadius: '4px',
                    '& fieldset': { borderColor: '#d0d5dd' },
                    '&:hover fieldset': { borderColor: '#005AA6' },
                    '&.Mui-focused fieldset': { borderColor: '#005AA6', borderWidth: '1.5px' },
                },
                '& .MuiOutlinedInput-input': { padding: '4px 8px' },
            }}
        />
    );
};

// Inline Select Cell — always shows a dropdown when editable
const InlineSelectCell = ({ value, options, editable, onSave }) => {
    const [localVal, setLocalVal] = useState(value ?? '');

    useEffect(() => {
        setLocalVal(value ?? '');
    }, [value]);

    const handleChange = (e) => {
        const newVal = e.target.value;
        setLocalVal(newVal);
        if (newVal !== (value ?? '')) {
            onSave(newVal);
        }
    };

    if (!editable) {
        return <Typography variant="body2" sx={{ fontSize: '13px' }}>{value || '--'}</Typography>;
    }

    return (
        <Select
            variant="standard"
            size="small"
            value={localVal}
            onChange={handleChange}
            onKeyDown={(e) => e.stopPropagation()}
            displayEmpty
            fullWidth
            disableUnderline
            sx={{ fontSize: '13px', '& .MuiSelect-select': { padding: '0 !important' } }}
        >
            <MenuItem value="" sx={{ fontSize: '13px' }}><em>--</em></MenuItem>
            {options.map((opt) => (
                <MenuItem key={opt} value={opt} sx={{ fontSize: '13px' }}>{opt}</MenuItem>
            ))}
        </Select>
    );
};

// Inline Date Cell — always shows a date picker when editable
const InlineDateCell = ({ value, editable, onSave }) => {
    const [localVal, setLocalVal] = useState(value ?? '');

    useEffect(() => {
        setLocalVal(value ?? '');
    }, [value]);

    const handleChange = (e) => {
        const newVal = e.target.value;
        setLocalVal(newVal);
    };

    const commit = () => {
        if (localVal !== (value ?? '')) {
            onSave(localVal || null);
        }
    };

    if (!editable) {
        return <Typography variant="body2" sx={{ fontSize: '13px' }}>{value || '--'}</Typography>;
    }

    return (
        <TextField
            variant="standard"
            size="small"
            type="date"
            value={localVal}
            onChange={handleChange}
            onBlur={commit}
            onKeyDown={(e) => e.stopPropagation()}
            fullWidth
            InputProps={{ disableUnderline: true, sx: { fontSize: '13px', padding: 0 } }}
            InputLabelProps={{ shrink: true }}
            sx={{ '& .MuiInput-root': { padding: 0 } }}
        />
    );
};

// Multi-Select Cell with inline Autocomplete editing
const MultiSelectCell = ({ value, options, editable, onSave }) => {
    const [editing, setEditing] = useState(false);
    const [localValue, setLocalValue] = useState([]);

    const values = useMemo(() => {
        if (!value) return [];
        if (Array.isArray(value)) return value;
        if (typeof value === 'string' && value.trim()) return value.split(',').map(v => v.trim());
        return [];
    }, [value]);

    const handleOpen = () => {
        if (!editable) return;
        setLocalValue(values);
        setEditing(true);
    };

    const handleSave = (newVal) => {
        setEditing(false);
        if (JSON.stringify(newVal) !== JSON.stringify(values)) {
            onSave(newVal);
        }
    };

    if (editing) {
        return (
            <Autocomplete
                multiple
                size="small"
                options={options || []}
                value={localValue}
                onChange={(e, newVal) => setLocalValue(newVal)}
                onClose={() => handleSave(localValue)}
                open={true}
                disableCloseOnSelect
                renderOption={(props, option, { selected }) => (
                    <li {...props} key={option}>
                        <Checkbox
                            icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                            checkedIcon={<CheckBoxIcon fontSize="small" />}
                            checked={selected}
                            sx={{ mr: 1, '&.Mui-checked': { color: '#005AA6' } }}
                        />
                        {option}
                    </li>
                )}
                renderTags={(selected, getTagProps) =>
                    selected.map((opt, index) => (
                        <Chip
                            {...getTagProps({ index })}
                            key={opt}
                            label={opt}
                            size="small"
                            sx={{
                                backgroundColor: lighten('#005AA6', 0.7),
                                color: '#0a2240',
                                fontWeight: 500,
                                height: '22px',
                            }}
                        />
                    ))
                }
                sx={{
                    width: '100%',
                    '& .MuiOutlinedInput-root': { padding: '2px 4px' },
                    '& .MuiAutocomplete-tag': { margin: '1px' },
                }}
                renderInput={(params) => (
                    <TextField {...params} variant="outlined" autoFocus />
                )}
            />
        );
    }

    // Display mode
    const displayBox = (
        <Box
            onClick={handleOpen}
            sx={{
                cursor: editable ? 'pointer' : 'default',
                width: '100%',
                minHeight: '30px',
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '3px',
                padding: '3px 8px',
                borderRadius: '4px',
                border: editable ? '1px solid #d0d5dd' : 'none',
                backgroundColor: editable ? '#f8fafc' : 'transparent',
                '&:hover': editable ? { borderColor: '#005AA6' } : {},
            }}
        >
            {values.length === 0 ? (
                <Typography variant="body2" sx={{ color: '#999', fontSize: '13px' }}>--</Typography>
            ) : (
                values.map((val) => (
                    <Chip
                        key={val}
                        label={val}
                        size="small"
                        sx={{
                            backgroundColor: lighten('#005AA6', 0.7),
                            color: '#0a2240',
                            fontWeight: 500,
                            height: '22px',
                        }}
                    />
                ))
            )}
        </Box>
    );

    return displayBox;
};
